/**
 * Currency formatting utilities for fixed-route cost display.
 *
 * All fixed-route cost figures are denominated in a single network currency
 * (see {@link NetworkCostSettings.currency}); no FX conversion is performed.
 */

/**
 * Format a monetary value in the given ISO-4217 currency.
 *
 * Falls back gracefully if the currency code is unknown to the runtime
 * (`Intl` throws on an invalid code) — in that case the code is shown as a prefix.
 * Non-finite values render as a zero amount rather than `NaN`/`∞`.
 *
 * @example formatCurrency(2240, 'USD') // "$2,240"
 * @example formatCurrency(2240.5, 'GBP') // "£2,241"
 */
export function formatCurrency(
  value: number | undefined | null,
  currency: string = 'USD',
  options: { maximumFractionDigits?: number } = {},
): string {
  const safeValue = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const maximumFractionDigits = options.maximumFractionDigits ?? 0;

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits,
      minimumFractionDigits: 0,
    }).format(safeValue);
  } catch {
    // Unknown/invalid currency code — degrade to "<CODE> 1,234".
    const amount = new Intl.NumberFormat(undefined, {
      maximumFractionDigits,
      minimumFractionDigits: 0,
    }).format(safeValue);
    return `${currency} ${amount}`;
  }
}

/**
 * Compact currency for headline figures, e.g. annual totals: `$640k`, `$5.3M`.
 * Below 1,000 it defers to {@link formatCurrency}.
 *
 * @example formatCurrencyCompact(640000, 'USD') // "$640k"
 * @example formatCurrencyCompact(5300000, 'USD') // "$5.3M"
 */
export function formatCurrencyCompact(
  value: number | undefined | null,
  currency: string = 'USD',
): string {
  const safeValue = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const abs = Math.abs(safeValue);

  if (abs < 1000) {
    return formatCurrency(safeValue, currency);
  }

  const sign = safeValue < 0 ? '-' : '';
  let scaled: number;
  let suffix: string;
  if (abs >= 1_000_000_000) {
    scaled = abs / 1_000_000_000;
    suffix = 'B';
  } else if (abs >= 1_000_000) {
    scaled = abs / 1_000_000;
    suffix = 'M';
  } else {
    scaled = abs / 1000;
    suffix = 'k';
  }

  // 1 decimal place, but drop a trailing ".0" (e.g. 640.0k -> 640k).
  const num = scaled >= 100 ? Math.round(scaled).toString() : scaled.toFixed(1).replace(/\.0$/, '');
  // Format "0" in the currency to capture the symbol/placement, then splice the scaled number in.
  const zero = formatCurrency(0, currency);
  const symbol = zero.replace(/[\d.,\s]/g, '');
  return `${sign}${symbol}${num}${suffix}`;
}
